import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import theme from '../../styles/theme';
import { formatTextContent } from '../../services/funnelService';

const Container = styled.div`
  height: 100vh;
  overflow-y: scroll;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  padding: ${theme.spacing.screenPadding};
  padding-top: calc(${theme.spacing.screenPadding} + 24px);
  padding-bottom: calc(${theme.spacing.screenPadding} + 120px); /* Extra bottom padding for mobile CTA access */
  
  /* Mobile-specific improvements */
  @media (max-width: ${theme.breakpoints.tablet}) {
    padding-bottom: calc(${theme.spacing.screenPadding} + 80px); /* Extra space to ensure CTA is scrollable + 20px buffer */
    height: 100vh;
    overflow-y: scroll;
    -webkit-overflow-scrolling: touch;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: ${theme.spacing.lg};
  min-height: calc(100vh - ${theme.spacing.screenPadding} * 2 - 24px - 120px); /* Ensure content is tall enough to scroll */
  
  /* Mobile-specific improvements */
  @media (max-width: ${theme.breakpoints.tablet}) {
    min-height: calc(100vh - ${theme.spacing.screenPadding} * 2 - 24px - 80px);
  }
`;

const Title = styled.h2`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h2};
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.lg};
`;

const Description = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.textLight};
  line-height: ${theme.typography.lineHeights.normal};
  margin-bottom: 8px;
`;

const ContinueButton = styled.button`
  background: ${theme.colors.primary};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.buttonPadding};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.touch};
  font-weight: ${theme.typography.weights.medium};
  cursor: pointer;
  transition: all ${theme.animation.durations.fast} ${theme.animation.easings.easeOut};
  min-height: 48px;
  box-shadow: ${theme.shadows.sm};
  /* Button is now in content flow, adjust spacing */
  margin-top: 0;
  margin-bottom: 24px;
  /* Better width handling for longer text */
  min-width: 200px;
  width: fit-content;
  max-width: 90%;
  align-self: center;
  /* Multi-line text support for very long text */
  white-space: normal;
  line-height: 1.2;
  text-align: center;
  /* Ensure proper touch targets on mobile */
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${theme.shadows.md};
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: ${theme.shadows.sm};
  }

  &:disabled {
    background: ${theme.colors.disabled};
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  /* Mobile-specific improvements */
  @media (max-width: ${theme.breakpoints.tablet}) {
    min-height: 56px; /* Larger touch target on mobile */
    padding: 1rem 1.5rem; /* More padding for better touch experience */
  }
  
  /* For very small screens, ensure text fits */
  @media (max-width: 320px) {
    font-size: 0.875rem;
    padding: 0.75rem 1rem;
    min-width: 150px;
  }
`;

const NoDataMessage = styled.div`
  background: ${theme.colors.neutral}20;
  border: 2px solid ${theme.colors.neutral}40;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  margin: ${theme.spacing.lg} 0;
  width: 100%;
  max-width: 400px;
  text-align: center;
`;

const NoDataTitle = styled.h3`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h3};
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.sm};
`;

const NoDataText = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.textLight};
  line-height: ${theme.typography.lineHeights.normal};
`;

// Bar Visualization Components (matching Mental Resilience style)
const BarContainer = styled.div`
  width: 100%;
  max-width: 80vw;
  margin: ${theme.spacing.md} auto ${theme.spacing.lg} auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  /* Allow scrolling on mobile */
  @media (max-width: ${theme.breakpoints.tablet}) {
    touch-action: manipulation;
  }
`;

const BarWrapper = styled.div`
  width: 80vw;
  max-width: 80vw;
  height: 40px;
  position: relative;
  background: #B8C5D1;
  border-radius: ${theme.borderRadius.lg};
  overflow: visible;
  box-shadow: ${theme.shadows.sm};
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    width: 80vw;
    max-width: 80vw;
  }
`;


const ScoreIndicator = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 12px;
  height: 12px;
  background: ${theme.colors.text};
  border: 3px solid white;
  border-radius: 50%;
  z-index: 10;
  box-shadow: ${theme.shadows.md};
  transition: all ${theme.animation.durations.normal} ${theme.animation.easings.easeOut};
`;

const ScoreLabel = styled.div`
  position: absolute;
  top: -43px;
  left: 50%;
  transform: translateX(-50%);
  background: ${theme.colors.text};
  color: white;
  padding: 6px 12px;
  border-radius: ${theme.borderRadius.sm};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  font-weight: ${theme.typography.weights.semiBold};
  white-space: nowrap;
  z-index: 11;
  box-shadow: ${theme.shadows.sm};
  
  /* Arrow pointing down */
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 4px solid transparent;
    border-top-color: ${theme.colors.text};
  }
`;

const BarLabels = styled.div`
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  transform: translateY(-50%);

  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  color: ${theme.colors.doryDarkerBlue};
  font-weight: ${theme.typography.weights.medium};
  pointer-events: none;
  z-index: 5;
`;

const BarLabel = styled.span`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  text-align: center;
  min-width: 20px;
`;

const LeftBarLabel = styled(BarLabel)`
  left: 10px;
`;

const RightBarLabel = styled(BarLabel)`
  right: 15px;
`;


// Win Rate Visualization Components
const WinRateContainer = styled.div`
  width: 100%;
  max-width: 80vw;
  margin: 0 auto 0px auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  /* Allow scrolling on mobile */
  @media (max-width: ${theme.breakpoints.tablet}) {
    touch-action: manipulation;
  }
`;

const WinRateTitle = styled.h4`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h3};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  margin-top: 40px;
  margin-bottom: ${theme.spacing.md};
  text-align: center;
  
  /* Make heading 20% smaller on small mobile devices */
  @media (max-width: ${theme.breakpoints.mobileLg}) {
    font-size: 0.8em;
  }
`;

const WinRateChart = styled.div`
  width: 100%;
  max-width: 80vw;
  height: 200px;
  position: relative;
  background: ${theme.colors.surface};
  padding: ${theme.spacing.md};
  box-sizing: border-box;
  margin-top: 40px;
`;

const WinRateBar = styled.div`
  position: absolute;
  bottom: 0;
  width: ${props => props.width}%;
  height: ${props => props.height}%;
  background: ${props => {
    if (props.isCurrent) return '#B8C5D1';
    if (props.isSuggested) return '#B8C5D1';
    return `${theme.colors.doryGreen}80`;
  }};
  border-radius: ${theme.borderRadius.sm} ${theme.borderRadius.sm} 0 0;
  transition: all ${theme.animation.durations.normal} ${theme.animation.easings.easeOut};
  left: ${props => props.left}%;
  
  &:hover {
    transform: scaleY(1.05);
    transform-origin: bottom;
  }
`;

const WinRateLabel = styled.div`
  position: absolute;
  bottom: -30px;
  left: 50%;
  transform: translateX(-50%);
  font-family: ${theme.typography.bodyFont};
  font-size: 12px;
  color: ${theme.colors.textLight};
  font-weight: ${props => props.isBold ? theme.typography.weights.bold : theme.typography.weights.regular};
`;

const WinRateValue = styled.div`
  position: absolute;
  top: -35px;
  left: 50%;
  transform: translateX(-50%);
  font-family: ${theme.typography.bodyFont};
  font-size: calc(${theme.typography.sizes.small} * 0.8);
  color: ${theme.colors.text};
  font-weight: ${props => props.isBold ? theme.typography.weights.bold : theme.typography.weights.regular};
`;

const SVGLine = styled.svg`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  pointer-events: none;
`;



const OptimalPaceRaceScreen = ({ data, onNext, userProgress = {} }) => {
  const [optimalPaceData, setOptimalPaceData] = useState(userProgress.calculatedMetrics?.optimalPace);
  
  // Update optimal pace data when userProgress changes
  useEffect(() => {
    const newOptimalPaceData = userProgress.calculatedMetrics?.optimalPace;
    if (newOptimalPaceData) {
      console.log('OptimalPaceRaceScreen: Received optimal pace data', newOptimalPaceData);
      setOptimalPaceData(newOptimalPaceData);
    }
  }, [userProgress]);



  
  // Process merge fields and format text content
  const htmlContent = formatTextContent(data.content?.text, userProgress);

  // Helper function to render the bar visualization
  const renderBarVisualization = (optimalHours) => {
    if (optimalHours === undefined || optimalHours === null) return null;

    // Calculate position of score indicator (0-18 scale)
    const scorePosition = Math.max(0, Math.min(18, optimalHours));
    // Adjust calculation to keep indicator within bar bounds (5% margin from each end)
    const indicatorLeft = `${5 + (scorePosition / 18) * 90}%`;

    // Conditionally hide labels based on Optimal Pace value
    const optimalPaceValue = Math.round(optimalHours);
    const hideZeroLabel = (optimalPaceValue === 0 || optimalPaceValue === 1);
    const hideEighteenLabel = (optimalPaceValue === 17 || optimalPaceValue === 18);

    return (
      <BarContainer>
        <BarWrapper>
          <BarLabels>
            {!hideZeroLabel && <LeftBarLabel>0</LeftBarLabel>}
            {!hideEighteenLabel && <RightBarLabel>18</RightBarLabel>}
          </BarLabels>
          
          <ScoreIndicator style={{ left: indicatorLeft }}>
            <ScoreLabel>{Math.round(optimalHours)}</ScoreLabel>
          </ScoreIndicator>
        </BarWrapper>
      </BarContainer>
    );
  };


  // Helper function to render the win rate visualization
  const renderWinRateVisualization = (optimalHours) => {
    if (optimalHours === undefined || optimalHours === null) return null;

    const currentPace = Math.round(optimalHours);
    const suggestedPace = Math.round((currentPace + 18) / 2);
    
    // Always show all bars to the right of Optimal Pace up to 18
    // When Optimal Pace is 15 or higher, show enough bars to the left to ensure at least 6 total bars
    let minPace, maxPace;
    maxPace = 18; // Always show all bars up to the maximum
    
    if (currentPace >= 15) {
      // For high Optimal Pace values, calculate how many bars to the left we need for at least 6 total
      const barsToRight = 18 - currentPace + 1; // Bars from current pace to 18
      const barsNeededToLeft = Math.max(0, 6 - barsToRight); // How many more bars we need to reach 6
      minPace = Math.max(0, currentPace - barsNeededToLeft);
    } else {
      // For lower Optimal Pace values, show one bar to the left
      minPace = Math.max(0, currentPace - 1);
    }
    
    const totalBars = maxPace - minPace + 1;
    const barWidth = 100 / totalBars;
    
    return (
      <WinRateContainer>
        <WinRateChart>
          {/* Render bars for the relevant range */}
          {Array.from({ length: totalBars }, (_, index) => {
            const pace = minPace + index;
            const winRate = Math.round((pace / 18) * 100);
            const barHeight = winRate;
            const barLeft = (index * barWidth);
            const isCurrent = pace === currentPace;
            const isSuggested = pace === suggestedPace;
            
            return (
              <WinRateBar
                key={pace}
                width={barWidth - 1} // Small gap between bars
                height={barHeight}
                left={barLeft}
                isCurrent={isCurrent}
                isSuggested={isSuggested}
              >
                <WinRateValue isBold={isCurrent || isSuggested}>{winRate}%</WinRateValue>
                <WinRateLabel isBold={isCurrent || isSuggested}>{pace}</WinRateLabel>
              </WinRateBar>
            );
          })}
          
          {/* Improvement line connecting current pace to suggested pace */}
          {(() => {
            const currentPace = Math.round(optimalHours);
            
            // Don't show improvement line when Optimal Pace is 18 (maximum)
            if (currentPace === 18) {
              return null;
            }
            
            const suggestedPace = Math.round((currentPace + 18) / 2);
            
            // Use the same range calculation as the bars
            let minPace, maxPace;
            maxPace = 18; // Always show all bars up to the maximum
            
            if (currentPace >= 15) {
              // For high Optimal Pace values, calculate how many bars to the left we need for at least 6 total
              const barsToRight = 18 - currentPace + 1; // Bars from current pace to 18
              const barsNeededToLeft = Math.max(0, 6 - barsToRight); // How many more bars we need to reach 6
              minPace = Math.max(0, currentPace - barsNeededToLeft);
            } else {
              // For lower Optimal Pace values, show one bar to the left
              minPace = Math.max(0, currentPace - 1);
            }
            
            const totalBars = maxPace - minPace + 1;
            const barWidth = 100 / totalBars;
            
            // Calculate horizontal centers - account for bar positioning and gaps
            const currentBarIndex = currentPace - minPace;
            const suggestedBarIndex = suggestedPace - minPace;
            const currentBarCenter = (currentBarIndex * barWidth) + ((barWidth - 1) / 2);
            const suggestedBarCenter = (suggestedBarIndex * barWidth) + ((barWidth - 1) / 2);
            
            // Calculate vertical centers (middle of each bar's height)
            const currentWinRate = Math.round((currentPace / 18) * 100);
            const suggestedWinRate = Math.round((suggestedPace / 18) * 100);
            
            // Bars are positioned from bottom: 0, so top is at (100 - barHeight)
            // Position line endpoints inside the bars, with right endpoint adjusted to reduce slope slightly
            const currentBarTop = 100 - currentWinRate + (currentWinRate * 0.25); // 25% down from top of current bar
            const suggestedBarTop = 100 - suggestedWinRate + (suggestedWinRate * 0.15); // 15% down from top of suggested bar (slightly reduced slope)
            
            
            // Calculate the diagonal line
            const horizontalDistance = suggestedBarCenter - currentBarCenter;
            const verticalDistance = suggestedBarTop - currentBarTop;
            
            // Calculate the angle for the diagonal line
            const angle = Math.atan2(verticalDistance, horizontalDistance) * (180 / Math.PI);
            
            // Calculate the actual length of the diagonal line
            const diagonalLength = Math.sqrt(horizontalDistance * horizontalDistance + verticalDistance * verticalDistance);
            
            
            return (
              <SVGLine>
                <line
                  x1={`${currentBarCenter}%`}
                  y1={`${currentBarTop}%`}
                  x2={`${suggestedBarCenter}%`}
                  y2={`${suggestedBarTop}%`}
                  stroke={theme.colors.doryDarkerBlue}
                  strokeWidth="6"
                />
                <circle
                  cx={`${currentBarCenter}%`}
                  cy={`${currentBarTop}%`}
                  r="6"
                  fill={theme.colors.doryDarkerBlue}
                />
                <circle
                  cx={`${suggestedBarCenter}%`}
                  cy={`${suggestedBarTop}%`}
                  r="6"
                  fill={theme.colors.doryDarkerBlue}
                />
              </SVGLine>
            );
          })()}
        </WinRateChart>
        <WinRateTitle>Performance Capacity by Pace</WinRateTitle>
      </WinRateContainer>
    );
  };

  // Check if we have optimal pace data
  if (!optimalPaceData) {
    return (
      <Container>
        <Content>
          {htmlContent && (
            <Description dangerouslySetInnerHTML={{ __html: htmlContent }} />
          )}
          
          <NoDataMessage>
            <NoDataTitle>No Data Available</NoDataTitle>
            <NoDataText>
              Complete a journal entry with hours worked to see your optimal pace calculation.
            </NoDataText>
          </NoDataMessage>
          
          {/* CTA button appears right after content */}
          <ContinueButton onClick={() => onNext?.(data.key)}>
            {data.content?.ctaText || 'Continue'}
          </ContinueButton>
        </Content>
      </Container>
    );
  }

  const { optimalHours } = optimalPaceData;

  return (
    <Container>
      <Content>
        {htmlContent && (
          <Description dangerouslySetInnerHTML={{ __html: htmlContent }} />
        )}
        
        {/* Bar visualization */}
        {renderBarVisualization(optimalHours)}
        
        {/* Current Performance Analysis */}
        <Description dangerouslySetInnerHTML={{
          __html: (() => {
            const currentPace = Math.round(optimalHours);
            const winPercentage = Math.round((currentPace / 18) * 100);
            
            let performanceText = "";
            if (currentPace <= 5) {
              performanceText = `You've only unlocked <strong>${winPercentage}%</strong> of your total possible performance.`;
            } else if (currentPace >= 6 && currentPace <= 10) {
              performanceText = `You've already unlocked <strong>${winPercentage}%</strong> of your total possible performance.`;
            } else if (currentPace >= 11 && currentPace <= 15) {
              performanceText = `Nicely done, you've already unlocked <strong>${winPercentage}%</strong> of your total possible performance.`;
            } else if (currentPace >= 16 && currentPace <= 17) {
              performanceText = `Impressive. You've already unlocked <strong>${winPercentage}%</strong> of your total available performance.`;
            } else if (currentPace === 18) {
              performanceText = `Incredible. You've currently unlocked <strong>${winPercentage}%</strong> of your total available performance.`;
            }
            
            return `Your Current Pace is <strong>${currentPace}</strong> out of 18. ${performanceText}`;
          })()
        }} />
        
        {/* Improvement Suggestion */}
        <Description dangerouslySetInnerHTML={{
          __html: (() => {
            const currentPace = Math.round(optimalHours);
            const suggestedPace = Math.round((currentPace + 18) / 2);
            const improvedWinPercentage = Math.round((suggestedPace / 18) * 100);
            
            let encouragementText = "";
            let improvementText = "";
            
            if (currentPace <= 10) {
              encouragementText = "<strong>You could be performing so much better.</strong>";
              improvementText = `Increase your Pace to ${suggestedPace} to unlock ${improvedWinPercentage}% of your performance.`;
            } else if (currentPace >= 11 && currentPace <= 14) {
              encouragementText = "<strong>You could be performing a lot better.</strong>";
              improvementText = `Increase your Pace to ${suggestedPace} to unlock ${improvedWinPercentage}% of your performance.`;
            } else if (currentPace >= 15 && currentPace <= 17) {
              encouragementText = "<strong>You could be performing even better.</strong>";
              improvementText = `Increase your Pace to ${suggestedPace} to unlock ${improvedWinPercentage}% of your performance.`;
            } else if (currentPace === 18) {
              encouragementText = "<strong>You're performing at a very high level.</strong>";
              improvementText = "Stay at the top of your game by continuing to support yourself.";
            }
            
            return `${encouragementText} ${improvementText}`;
          })()
        }} />
        
        {/* Win Rate Visualization */}
        {renderWinRateVisualization(optimalHours)}
        
        {/* CTA button appears right after content */}
        <ContinueButton onClick={() => onNext?.(data.key)}>
          {data.content?.ctaText || 'Continue'}
        </ContinueButton>
      </Content>
    </Container>
  );
};

export default OptimalPaceRaceScreen;
